import { inject, signal, Service } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, tap } from 'rxjs';

import { IndexedDbService } from '@ideals/services/indexed-db';
import { downloadFile } from '@ideals/utils/download-file';

import { DiagnosticStatus, ErrorDetails } from '../../models';
import { TROUBLESHOOTING } from '../../troubleshooting.token';

@Service()
export class TroubleshootingService {
  readonly #indexedDbService = inject(IndexedDbService);
  readonly #services = inject(TROUBLESHOOTING);
  readonly #translateService = inject(TranslateService);

  readonly diagnosticErrorDetails = signal<ErrorDetails[]>([]);
  readonly diagnosticStatus = signal<DiagnosticStatus>('Default');

  async downloadReport(): Promise<void> {
    const report = {
      ...this.#services.reduce((acc, service) => ({
        ...acc,
        ...service.getErrorReports(),
      }), {}),
      logs: await this.#indexedDbService.getItem('logs') ?? [],
    };

    const space = 2;
    const blob = new Blob([JSON.stringify(report, null, space)], { type: 'application/json' });
    const name = this.#translateService.instant('help_modal.TEXT.Troubleshooting') as string;
    const date = new Date().toISOString();

    downloadFile(blob, `${name}\u2014${date}.json`);
  }

  formatErrors(): string {
    return this.diagnosticErrorDetails().map((error) => {
      const values = error.values
        ?.replace(/\s+/g, '')
        ?.replace(/;/g, '\n') ?? '';

      return [
        error.title,
        error.description,
        values,
        error.text,
      ]
        .filter((v) => v)
        .join('\n');
    }).join('\n\n');
  }

  reset(): void {
    this.diagnosticErrorDetails.set([]);
    this.diagnosticStatus.set('Default');
  }

  runDiagnostic(): Observable<void[]> {
    this.diagnosticStatus.set('InProgress');

    return forkJoin(this.#services.map((service) => service.run()))
      .pipe(
        tap(() => {
          const statuses = this.#services.map((service) => service.getStatus());

          const parentDetails: ErrorDetails = {
            title: this.#translateService.instant('help_modal.TEXT.Check_failed') as string,
            description: this.#translateService.instant('help_modal.TEXT.We_ran_a_quick_check') as string,
            dataIdeals: 'copy-issues',
          };

          const errorDetails = statuses.includes('Error')
            ? this.#services
              .filter((service) => service.getStatus() === 'Error')
              .map((service) => service.getErrorDetails())
            : [];

          this.diagnosticErrorDetails.set(errorDetails.length > 1 ? [parentDetails, ...errorDetails] : errorDetails);
          this.diagnosticStatus.set(statuses.includes('Error') ? 'Error' : 'Completed');
        }),
      );
  }
}
